define('core/models/User',[
    'jquery',
    'underscore',
    'moment',

    'core/config',
    'core/time',
    'core/utils',
    'core/strings',
    'core/api',

    'core/models/BaseUser',
], function (
    $,
    _,
    moment,

    config,
    time,
    utils,
    strings,
    api,
    BaseUser
) {
    'use strict';

    var gettext = strings.get;

    function addError(validationErrors, name, error) {
        validationErrors[name] = validationErrors[name] || [];
        validationErrors[name].push(error);
    }

    // WARNING: Do not create User objects directly. Use UniqueModel instead.
    var User = BaseUser.extend({
        url: api.getURL('users/details'),

        validate: function (attrs) {
            // THOUGHT: I'm not sure that we should be calling gettext
            //          from the model. I think we should have the view
            //          do that. Coupling, etc.
            var validationErrors = {};

            if (attrs.display_name)
                attrs.display_name = $.trim(attrs.display_name);

            if (!attrs.display_name)
                addError(validationErrors, 'display_name', gettext('Please enter your name.'));

            if (!attrs.email)
                addError(validationErrors, 'email', gettext('Please enter your email address.'));

            if (!utils.validateEmail(attrs.email))
                addError(validationErrors, 'email', gettext('Invalid email address.'));

            if (this.isNew()) {
                if (!attrs.password)
                    addError(validationErrors, 'password', gettext('Please enter a password.'));

                else if (attrs.password.length < User.MIN_PASSWORD_LEN)
                    addError(validationErrors, 'password', gettext('Password must have at least 6 characters.'));
            }

            if (attrs.name) {
                if (attrs.name.length < User.MIN_NAME_LEN) {
                    addError(validationErrors, 'name', strings.interpolate(
                        gettext('Name must have at least %(minLength)s characters.'),
                        { minLength: User.MIN_NAME_LEN }
                    ));
                }

                if (attrs.name.length > User.MAX_NAME_LEN) {
                    addError(validationErrors, 'name', strings.interpolate(
                        gettext('Name must have less than %(maxLength)s characters.'),
                        { maxLength: User.MAX_NAME_LEN }
                    ));
                }
            }

            if (attrs.location) {
                if (attrs.location.length > User.MAX_LOCATION_LEN) {
                    addError(validationErrors, 'location', strings.interpolate(
                        gettext('Location must have less than %(maxLength)s characters.'),
                        { maxLength: User.MAX_LOCATION_LEN }
                    ));
                }
            }

            if (attrs.url) {
                if (attrs.url.length > User.MAX_URL_LEN) {
                    addError(validationErrors, 'url', strings.interpolate(
                        gettext('Site must have less than %(maxLength)s characters.'),
                        { maxLength: User.MAX_URL_LEN }
                    ));
                }

                if (!utils.isUrl(attrs.url))
                    addError(validationErrors, 'url', gettext('Please enter a valid site.'));
            }

            if (!_.isEmpty(validationErrors))
                return validationErrors;
        },

        prepareFetchOptions: function (options) {
            options = options ? _.clone(options) : {};

            var data = {};

            if (this.get('id'))
                data.user = this.get('id');
            else if (this.get('username'))
                data.user = 'username:' + this.get('username');

            _.extend(data, options.data);
            options.data = data;

            return options;
        },

        fetch: function (options) {
            options = this.prepareFetchOptions(options);
            return BaseUser.prototype.fetch.call(this, options);
        },

        parse: function (response) {
            return response.response || response;
        },

        register: function (options) {
            var self = this;
            options = options || {};


            return api.call('internal/users/register.json', {
                data: _.extend(this.toRegisterJSON(), {
                    gRecaptchaResponse: options.gRecaptchaResponse,
                }),
                method: 'POST',
                success: function (data) {
                    api.call('users/acceptTerms', {
                        method: 'POST',
                    });
                    self.set(_.extend({}, data.response, {
                        // To speed up the signup process, assume the acceptTerms endpoint
                        // call will succeed
                        hasAcceptedGdprTerms: true,
                    }));
                    if (options.success)
                        options.success(data);
                },
                error: options.error,
            });
        },

        /*
         * This method uses FormData which isn't supported in all browsers.
         * The Onboard.ProfileSectionView uses this method but checks to see
         * if FormData is supported first.
         */
        saveAvatar: function (fileInput) {
            var formData = new window.FormData();
            formData.append('avatar_file', fileInput);
            formData.append('api_key', config.keys.api);

            return api.call('internal/users/updateAvatar.json', {
                method: 'post',
                data: formData,
                cache: false,
                contentType: false,
                processData: false,
            });
        },

        saveProfile: function () {
            return api.call('users/updateProfile.json', {
                method: 'POST',
                data: {
                    name: this.get('name'),
                    about: this.get('about'),
                    location: this.get('location'),
                    url: this.get('url'),
                },
            });
        },

        toRegisterJSON: function () {
            return _.pick(this.toJSON(), 'display_name', 'email', 'password');
        },

        isSession: function (session) {
            return session.user.id && session.user.id === this.id;
        },

        isEditable: function (session) {
            return this.isSession(session) && !this.get('remote');
        },

        toJSON: function (options) {
            options = options || {};

            var json = BaseUser.prototype.toJSON.call(this);
            var thread = this.collection && this.collection.thread;

            json.thread.canModerate = Boolean(thread && thread.isModerator(this));

            // Optionally augment output JSON depending on active session
            if (options.session) {
                json.isSession = this.isSession(options.session);
                json.isEditable = this.isEditable(options.session);
            }
            return json;
        },

        // Calls users/follow if state is true ("following"), users/unfollow
        // if state is false ("not following")
        _changeFollowState: function (state) {
            this.set({
                isFollowing: state,
                numFollowers: Math.max(
                    0,
                    this.get('numFollowers') + (state ? 1 : -1)
                ),
            });

            var endpoint = 'users/' + (state ? 'follow' : 'unfollow');

            // NOTE: If the server returns 200 with a *different* state value
            //       than requested, we are ignoring that value. This should
            //       be exceedingly rare (race condition).

            var self = this;

            return api.call(endpoint + '.json', {
                data: { target: this.get('id') },
                method: 'POST',
                success: function (resp) {
                    self.trigger('sync', self, resp, {});
                },
            });
        },

        follow: function () {
            return this._changeFollowState(true);
        },

        unfollow: function () {
            return this._changeFollowState(false);
        },

        _changeBlockState: function (state) {
            var endpoint = 'users/block/' + (state ? 'create' : 'delete');

            var self = this;
            return api.call(endpoint + '.json', {
                data: { user: this.get('id') },
                method: 'POST',
                success: function (resp) {
                    self.set(resp.response);
                },
            });
        },

        block: function () {
            return this._changeBlockState(true);
        },

        unblock: function () {
            return this._changeBlockState(false);
        },

        report: function (reason) {
            var self = this;

            return api.call('users/report.json', {
                data: {
                    reason: reason,
                    user: this.get('id'),
                },
                method: 'POST',
                success: function () {
                    self.set('isFlagged', true);
                },
            });
        },

        toggleFollowState: function () {
            return this._changeFollowState(!this.get('isFollowing'));
        },

        /*
         * Checks to see if user was registered within a certain period.
         */
        registeredLessThan: function (num, period) {
            var joinedAtUtc = time.assureTzOffset(this.get('joinedAt'));
            var timePeriodAgo = moment().subtract(num, period);
            return moment(joinedAtUtc).isAfter(timePeriodAgo);
        },

        registeredToday: function () {
            return this.registeredLessThan(1, 'day'); // ago
        },

        registeredThisWeek: function () {
            return this.registeredLessThan(1, 'week'); // ago
        },

        /**
         * Whether or not this user has completed onboarding in home.
         * @returns {boolean}
         */
        shouldHomeOnboard: function () {
            return !this.get('homeOnboardingComplete');
        },

        /**
         * Sets the flag for whether or not this user has completed
         * onboarding in home.
         * @param {boolean} isComplete denotes if user has completed home onboarding
         */
        setHomeOnboardComplete: function (isComplete) {
            this.updateFlags({
                'homeOnboardingComplete': isComplete,
            });

            // Sometimes, immediately after completing onboarding the app will receive this
            // user's details through an api call and they have a falsey, out of date value for
            // homeOnboardingComplete which overwrites this change. Since it's not possible
            // to undo the completion of homeOnboarding from within the app, it's safe to
            // persist the truthy value.
            if (isComplete) {
                this.listenTo(this, 'change:homeOnboardingComplete',
                    _.bind(this.set, this, 'homeOnboardingComplete', isComplete, { silent: true }));
            }
        },

        /*
         * updateFlags expects an object with boolean values for flags:
         * {
         *     homeOnboardingComplete: true,
         *     homeFeedOnboardingComplete: false
         * }
         *
         * It will convert the boolean values to 1s and 0s for the api call
         * as the UpdateFlags endpoint expects that.
         */
        updateFlags: function (flags) {
            this.set(flags);

            return api.call('internal/users/updateFlags.json', {
                data: _.mapObject(flags, function (val) {
                    return val ? 1 : 0;
                }),
                method: 'POST',
            });
        },
    }, {
        MIN_PASSWORD_LEN: 6,
        MIN_NAME_LEN: 2,
        MAX_NAME_LEN: 30,
        MAX_LOCATION_LEN: 255,
        MAX_URL_LEN: 200,
    });

    return User;
});

